import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SurveyModel } from 'survey-core';
import 'survey-core/survey-core.min.css';
import { Survey } from 'survey-react-ui';

import { useTranslation } from '@/hooks/useTranslation';
import {
  calculateCompletionTime,
  submitSurveyFeedback,
} from '@/services/feedbackApi';
import { loggingUtility } from '@/utils/loggingUtility';
import { useSurveyProgress } from '../hooks/useSurveyProgress';
import { SurveyHeader } from './SurveyHeader';
import './SurveyRenderer.css';

interface SurveyRendererProps {
  survey: SurveyModel;
  surveyId: string;
  collectedMetadata: Record<string, unknown> | null;
  onComplete: (responses: Record<string, unknown>, responseId: string) => void;
}

export const SurveyRenderer: React.FC<SurveyRendererProps> = ({
  survey,
  surveyId,
  collectedMetadata,
  onComplete,
}) => {
  const { t } = useTranslation();
  const [surveyStartTime, setSurveyStartTime] = useState<Date | null>(null);
  const [hasTrackedStart, setHasTrackedStart] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const saveProgressTimeoutRef = useRef<number | null>(null);
  const [currentPageKey, setCurrentPageKey] = useState(0);
  const [progressPercent, setProgressPercent] = useState(0);

  const { surveyResponseId, saveProgress, sessionId } = useSurveyProgress({
    surveyId,
    surveyStartTime,
    collectedMetadata: null,
  });

  // Track survey start when component mounts
  useEffect(() => {
    if (!hasTrackedStart && surveyId) {
      const startTime = new Date();
      setSurveyStartTime(startTime);
      setHasTrackedStart(true);
    }
  }, [hasTrackedStart, surveyId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveProgressTimeoutRef.current) {
        clearTimeout(saveProgressTimeoutRef.current);
      }
    };
  }, []);

  const handleCurrentPageChanged = useCallback(
    async (sender: SurveyModel) => {
      // Clear any pending debounced save
      if (saveProgressTimeoutRef.current) {
        clearTimeout(saveProgressTimeoutRef.current);
        saveProgressTimeoutRef.current = null;
      }

      // Update the key to force React to remount the Survey component
      // This prevents Google Translate DOM modifications from conflicting with React's reconciliation
      setCurrentPageKey(prev => prev + 1);

      const visibleQuestions = sender.getAllQuestions(true);
      const totalVisible = visibleQuestions.length || 1;
      let lastAnsweredQuestionId: string | null = null;
      let answeredVisible = 0;
      visibleQuestions.forEach(question => {
        if (!question.isEmpty()) {
          lastAnsweredQuestionId = question.name;
          answeredVisible += 1;
        }
      });
      setProgressPercent((answeredVisible / totalVisible) * 100);

      if (lastAnsweredQuestionId) {
        await saveProgress(lastAnsweredQuestionId);
      }
    },
    [saveProgress]
  );

  // Handle survey completion
  const handleSurveyComplete = useCallback(
    async (sender: SurveyModel) => {
      if (!surveyId) {
        loggingUtility.error('Survey ID is missing in handleSurveyComplete');
        return;
      }

      setIsSubmittingFeedback(true);

      try {
        const endTime = new Date();
        const completionTimeMs = surveyStartTime
          ? calculateCompletionTime(surveyStartTime, endTime)
          : undefined;

        // Use pre-collected metadata and merge with survey-specific data
        const surveySpecificMetadata = {
          startTime: surveyStartTime?.toISOString(),
          endTime: endTime.toISOString(),
          completionTimeMs,
          currentPageNo: sender.currentPageNo,
          totalPages: sender.visiblePages?.length || 0,
        };

        // Merge pre-collected metadata with survey-specific data
        const metadata = {
          ...collectedMetadata,
          ...surveySpecificMetadata,
        };
        const submissionResponse = await submitSurveyFeedback(surveyId, {
          responseId: surveyResponseId || undefined,
          sessionId,
          response: sender.data,
          isCompleted: true,
          completionTimeMs,
          startTime: surveyStartTime?.toISOString(),
          endTime: endTime.toISOString(),
          metadata,
        });

        // Notify parent component with responses and responseId
        onComplete(sender.data, submissionResponse.responseId);
      } catch (error) {
        loggingUtility.error('Error submitting survey feedback:', error);
        throw error;
      } finally {
        setIsSubmittingFeedback(false);
      }
    },
    [
      surveyId,
      surveyResponseId,
      sessionId,
      surveyStartTime,
      collectedMetadata,
      onComplete,
    ]
  );

  // Add arrow to navigation button text for visual clarity
  // This helps users identify the action even when Google Translate modifies text
  useEffect(() => {
    // SurveyJS provides default text - just append arrow if not already present
    const nextText = survey.pageNextText || 'Next';
    const completeText = survey.completeText || 'Complete';

    if (!nextText.includes('→')) {
      survey.pageNextText = `${nextText} →`;
    }
    if (!completeText.includes('→')) {
      survey.completeText = `${completeText} →`;
    }
  }, [survey]);

  // Extract title and description from survey
  const surveyTitle = survey.title || '';
  const surveyDescription = survey.description || '';

  if (isSubmittingFeedback) {
    return (
      <main className='min-h-screen bg-gray-50 flex items-center justify-center'>
        <div className='text-center p-8' aria-live='polite'>
          <i
            className='pi pi-spin pi-spinner text-4xl text-blue-500 mb-4'
            aria-hidden='true'
          ></i>
          <h3 className='text-2xl font-semibold text-gray-900 mb-2'>
            {t('survey.submittingFeedback')}
          </h3>
          <p className='text-gray-600'>{t('survey.submittingDescription')}</p>
        </div>
      </main>
    );
  }

  return (
    <main className='min-h-screen bg-white'>
      <div className='mx-auto p-4 max-w-[1320px]'>
        <div className='p-4 rounded-lg bg-stone-100 pt-10'>
          <SurveyHeader
            title={surveyTitle}
            description={surveyDescription}
            progressPercent={progressPercent}
          />
          <Survey
            key={currentPageKey}
            model={survey}
            onComplete={handleSurveyComplete}
            onCurrentPageChanging={handleCurrentPageChanged}
          />
        </div>
      </div>
    </main>
  );
};
